<template>
  <div
    ref="linkSelection"
    class="LinkSelection">
    <div class="details">
      <div class="detail">
        <Icon name="text-fields">
          "{{ link.label }}"
        </Icon>
        <Btn class="danger xs ib light" @click="$emit('remove')">
          <Icon name="cross" />
        </Btn>
      </div>
      <div v-if="link.reference" class="detail">
        <Icon :alt="link.reference" name="search">
          {{ link.reference }}
        </Icon>
      </div>
    </div>
    <div v-if="link.options && link.options.length">
      <div
        class="option"
        @click="toggleOptions"
        @keypress.enter="toggleOptions">
        <Set
          :class="{ open: isOptionsOpen }"
          :label="optionSelected.title">
          {{ optionSelected.url }}
        </Set>
        <div class="action">
          <Icon :name="isOptionsOpen ? 'up' : 'down'" />
        </div>
      </div>
      <div v-if="isOptionsOpen" class="options">
        <div
          v-for="({ title, url }) in link.options"
          :key="url"
          class="option">
          <div
            class="link"
            @click="setUrl(url)"
            @keypress.enter="setUrl(url)">
            <Set
              :class="{ active: url === optionSelected.url }"
              :label="title">
              {{ url }}
            </Set>
          </div>
          <div
            class="action"
            @click="toggleOptions"
            @keypress.enter="toggleOptions">
            <a :href="url" target="_blank">
              <Icon name="link" />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="linkTextField">
      <Field
        :class="{ notSaved: linkUrl !== link.url }"
        :placeholder="$t('linkUrl')"
        :value.sync="linkUrl" />
      <Icon
        v-if="linkUrl !== link.url"
        name="check"
        @click="$emit('replace', linkUrl)" />
    </div>
  </div>
</template>

<script>
import { actions } from '@utils'
import { propEq } from 'rambda'

export default {
  props: {
    link: Object,
    linksActive: Array,
  },
  data() {
    return {
      isOptionsOpen: false,
      linkUrl: this.link.url,
    }
  },
  computed: {
    optionSelected() {
      if (this.linksActive.every(({ label }) => label !== this.link.label)) return {}

      return this.link.options
        .find(({ url }) => this.linksActive.some(propEq('url', url))) || {}
    },
  },
  methods: {
    toggleOptions() {
      this.isOptionsOpen = !this.isOptionsOpen
    },
    closeOnBlur(event) {
      if (this.isOptionsOpen && !this.$refs.linkSelection.contains(event.target)) {
        this.isOptionsOpen = false
      }
    },
    setUrl(url) {
      this.isOptionsOpen = false
      this.$emit('replace', url)
    },
    ...actions(),
  },
  async beforeMount() {
    this.$offEvents = await this.ON({ click: this.closeOnBlur })
  },
  beforeDestroy() {
    this.$offEvents()
  },
}
</script>

<style lang="scss">
.LinkSelection {
  position: relative;

  .linkTextField {
    position: relative;

    .notSaved {
      input {
        padding-right: 1.5rem;
      }
    }

    .Icon {
      align-items: center;
      background-color: $info-lighter;
      border-radius: 0 $radius $radius 0;
      cursor: pointer;
      display: flex;
      font-size: $h7;
      height: 100%;
      justify-content: center;
      position: absolute;
      right: 0;
      top: 0;
      width: 1.4rem;

      &:hover {
        background: $info;
        color: $white;

        a {
          color: inherit;
        }
      }
    }
  }

  .details {
    margin: 0.5rem 0;

    > .detail {
      display: flex;
      justify-content: space-between;

      > .Icon {
        @include ellipsis;
      }
    }
  }

  .action {
    align-items: center;
    background: $info-lighter;
    cursor: pointer;
    display: flex;
    justify-content: center;
    width: 1.5rem;

    .Icon {
      height: auto;
    }

    a {
      border: none;
    }

    &:hover {
      background: $info;
      color: $white;

      a {
        color: inherit;
      }
    }
  }

  .option {
    border: solid 1px $border-color;
    cursor: pointer;
    display: flex;
    min-height: 2.75rem;
    overflow: hidden;
    position: relative;
    width: 100%;

    .link {
      min-width: 0;
      overflow: hidden;
      width: 100%;
    }

    .Set {
      min-width: 0;
      padding: 0.5rem;
      width: 100%;

      .label {
        @include ellipsis;
      }

      .value {
        font-size: $h6;
      }

      &.active, &.open, &:hover {
        background: $info-lighter;

        .label {
          color: $info-dark;
        }
      }

      &:active {
        background: $info-light;
      }
    }
  }

  .options {
    background: $white;
    border: solid 1px $info-light;
    border-radius: 0 0 $radius $radius;
    box-shadow: 0 3px $white;
    max-height: 15rem;
    overflow: hidden;
    position: absolute;
    width: 100%;
    z-index: 2;

    .option {
      border-top: 0;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "linkUrl": "Link url"
  }
}
</i18n>
